import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';

interface TrackResult {
  refCode: string;
  status: string;
  statusLabel: string;
  nextStep: string;
  submittedAt: string;
  updatedAt: string;
}

const TONE: Record<string, string> = {
  new: 'blue',
  screening: 'amber',
  accepted: 'green',
  rejected: '',
  duplicate: '',
};

/**
 * ติดตามสถานะด้วยรหัสอ้างอิง — ไม่ต้องล็อกอิน
 *
 * หน้านี้จงใจไม่แสดงชื่อ ที่อยู่ เบอร์โทร หรือรายละเอียดคำขอเลย เพราะมือถือ
 * ในครัวเรือนยากจนมักถูกใช้ร่วมกันหรือยืมกันมา — ถ้าโชว์ข้อมูลส่วนตัวไว้
 * คนที่หยิบเครื่องขึ้นมาต่อจะเห็นหมด ให้เห็นแค่ว่าเรื่องถึงไหนแล้วก็พอ
 */
export default function TrackRequest() {
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState(params.get('code') ?? '');
  const [result, setResult] = useState<TrackResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(async (raw: string) => {
    const clean = raw.trim().toUpperCase();
    if (!clean) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await api<TrackResult>(`/help-requests/track/${encodeURIComponent(clean)}`, {
          anonymous: true,
        }),
      );
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(
        status === 404
          ? 'ไม่พบรหัสนี้ กรุณาตรวจสอบตัวอักษรอีกครั้ง (ตัวเลข 0 กับตัวอักษร O ไม่ได้ใช้ในรหัส)'
          : 'ตรวจสอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
      );
    } finally {
      setBusy(false);
    }
  }, []);

  // มาจากลิงก์ที่มีรหัสติดมา → ค้นให้เลย ไม่ต้องให้กดซ้ำ
  useEffect(() => {
    const fromUrl = params.get('code');
    if (fromUrl) void lookup(fromUrl);
    // ตั้งใจให้ทำงานครั้งเดียวตอนเปิดหน้า
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function search() {
    setParams(code.trim() ? { code: code.trim().toUpperCase() } : {});
    void lookup(code);
  }

  return (
    <div className="screen">
      <div className="topbar">
        <h1>ติดตามสถานะคำขอ</h1>
      </div>

      <div className="card">
        <div className="field">
          <label htmlFor="code">รหัสอ้างอิง</label>
          <input
            id="code"
            className="mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') search();
            }}
            placeholder="HR-XXXX-XXXX"
            autoCapitalize="characters"
            style={{ fontSize: 20, letterSpacing: 1 }}
          />
          <div className="hint">รหัสที่ได้ตอนยื่นคำขอ ขึ้นต้นด้วย HR-</div>
        </div>
        <button className="btn block" disabled={busy || !code.trim()} onClick={search}>
          {busy ? 'กำลังตรวจสอบ…' : 'ตรวจสอบ'}
        </button>
      </div>

      {error && <div className="alert error">{error}</div>}

      {result && (
        <div className="card">
          <div className="mono muted" style={{ fontSize: 14 }}>
            {result.refCode}
          </div>
          <div style={{ margin: '10px 0' }}>
            <span className={`badge ${TONE[result.status] ?? ''}`} style={{ fontSize: 16 }}>
              {result.statusLabel}
            </span>
          </div>
          <p style={{ marginBottom: 12 }}>{result.nextStep}</p>
          <div className="muted" style={{ fontSize: 13 }}>
            ยื่นเมื่อ {formatDate(result.submittedAt)}
            <br />
            อัปเดตล่าสุด {formatDate(result.updatedAt)}
          </div>
        </div>
      )}

      <Link className="btn block secondary" to="/help" style={{ marginTop: 8 }}>
        ยื่นคำขอใหม่
      </Link>
    </div>
  );
}
